<script setup lang="ts">
import type { IconName } from "../Icon/icons";
import type { IconTileSize, IconTileTone } from "./VIconTile.types";
import { computed } from "vue";
import VIcon from "../Icon/VIcon.vue";

defineOptions({
  name: "VIconTile",
});

/**
 * A rounded, tinted square with one icon: the top of an empty table or of a dashed empty section (56px), the top of a
 * centred confirm (48px), the leading icon of a dialog header (44px), the logo slot of a list row (40px), the resource
 * of a permission row or the icon of a notice or status card (32px), and the bullet of a consequence list or the icon
 * of a stat tile (28px).
 */
const {
  icon,
  tone = "primary",
  size = "lg",
} = defineProps<{
  icon: IconName
  tone?: IconTileTone
  size?: IconTileSize
}>();

const TONE_CLASS: Record<IconTileTone, string> = {
  "neutral": "bg-gray-100 text-gray-800 dark:bg-dark-700 dark:text-gray-200",
  "primary": "bg-primary-50 text-primary-600 dark:bg-primary-500/15 dark:text-primary-400",
  "danger": "bg-red-50 text-red-600 dark:bg-red-500/15 dark:text-red-400",
  "warning": "bg-orange-50 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300",
  "success": "bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-300",
  "caution": "bg-orange-100 text-warning-800 dark:bg-orange-500/20 dark:text-orange-300",
  "surface": "border border-gray-200 bg-white text-gray-700 dark:border-dark-600 dark:bg-dark-800 dark:text-gray-300",
  "muted": "bg-gray-100 text-gray-500 dark:bg-dark-700 dark:text-gray-400",
  "info": "bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300",
  "accent": "bg-purple-50 text-purple-600 dark:bg-purple-500/15 dark:text-purple-300",
  // One step darker than the light tint, for a tile on a surface that already has that tint (a tinted VAlert).
  "primary-strong": "bg-primary-100 text-primary-900 dark:bg-primary-500/25 dark:text-primary-200",
  "danger-strong": "bg-red-100 text-red-800 dark:bg-red-500/25 dark:text-red-200",
};

const SIZE_CLASS: Record<IconTileSize, string> = {
  "xs": "size-7 rounded-lg text-sm",
  "sm": "size-8 rounded-lg text-base",
  "md": "size-10 rounded-[10px] text-lg",
  "lg": "size-11 rounded-xl text-[22px]",
  "xl": "size-12 rounded-[14px] text-2xl",
  "2xl": "size-14 rounded-2xl text-[26px]",
};

const tileClass = computed(() => [TONE_CLASS[tone], SIZE_CLASS[size]]);
</script>

<template>
  <span class="flex shrink-0 items-center justify-center" :class="tileClass" aria-hidden="true">
    <VIcon :name="icon" />
  </span>
</template>
